import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { API_ENDPOINTS } from '../../config/api';
import api from '../../utils/apiClient';
import { motion } from 'framer-motion';
import { FaRobot, FaTrophy, FaUser, FaGamepad, FaChevronLeft, FaClock, FaUsers, FaArrowRight } from 'react-icons/fa';

const difficultyConfig = {
  easy: {
    gradient: 'from-green-500 to-emerald-600',
    glow: 'shadow-[0_0_15px_rgba(16,185,129,0.5)]',
    textGradient: 'from-green-400 to-emerald-500',
    icon: <FaRobot className="mr-1" />
  },
  medium: {
    gradient: 'from-yellow-500 to-amber-600',
    glow: 'shadow-[0_0_15px_rgba(245,158,11,0.5)]',
    textGradient: 'from-yellow-400 to-amber-500',
    icon: <FaRobot className="mr-1" />
  },
  hard: {
    gradient: 'from-red-500 to-rose-600',
    glow: 'shadow-[0_0_15px_rgba(244,63,94,0.5)]',
    textGradient: 'from-red-400 to-rose-500',
    icon: <FaRobot className="mr-1" />
  }
};

const QuestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshWalletBalance } = useAuth();
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  useEffect(() => {
    const fetchQuest = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const endpoint = API_ENDPOINTS.quests.details(id);
        if (!endpoint) {
          setLoading(false);
          return;
        }

        const data = await api.get(endpoint);
        setQuest(data);

        // Fetch leaderboard separately to get the most up-to-date data
        try {
          const leaderboardEndpoint = API_ENDPOINTS.quests.leaderboard(id);
          const leaderboardData = await api.get(leaderboardEndpoint);
          setLeaderboard(leaderboardData || []);
        } catch (leaderboardError) {
          console.error('Error fetching leaderboard:', leaderboardError);
          // If leaderboard fetch fails, fall back to any leaderboard data in the quest
          setLeaderboard(data.leaderboard || []);
        }
      } catch (error) {
        console.error('Error fetching quest:', error);
        if (error.response?.status === 500) {
          // Handle server errors silently since we show the "Invalid Quest ID" message
          setQuest(null);
        } else {
          toast.error('Failed to load quest');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuest();
    } else {
      setLoading(false);
    }

  }, [id]);

  useEffect(() => {
    // Check for game completion from URL params after quest data is loaded
    if (quest) {
      const params = new URLSearchParams(window.location.search);
      const won = params.get('won') === 'true';
      const rewardError = params.get('rewardError');
      const generalError = params.get('error');

      // Only process the game completion if there are params but we haven't shown a notification yet
      if (params.has('won') && !window.sessionStorage.getItem(`quest-${id}-completed`)) {
        console.log('Processing game completion notification');

        // Mark this quest as having shown the completion notification
        window.sessionStorage.setItem(`quest-${id}-completed`, 'true');

        // Display any specific reward errors
        if (rewardError) {
          console.error('Reward processing error:', rewardError);
          toast.error(`Quest completed, but reward failed: ${rewardError}`, { duration: 6000 });
        }

        // Display general errors
        if (generalError) {
          console.error('Game completion error:', generalError);
          if (generalError === 'auth') {
            toast.error('Authentication error occurred', { duration: 4000 });
          } else if (generalError === 'reporting') {
            toast.error('Error reporting game result to server', { duration: 4000 });
          }
        }

        // Immediately refresh wallet balance with multiple retries
        const refreshAttempts = async (maxAttempts = 3) => {
          let attempts = 0;
          let success = false;

          while (attempts < maxAttempts && !success) {
            try {
              console.log(`Refreshing wallet balance (attempt ${attempts + 1}/${maxAttempts})`);
              const updatedUser = await refreshWalletBalance();
              console.log('Wallet balance refreshed successfully:', updatedUser);
              success = true;

        // Show appropriate toast based on game result
        if (won && !rewardError) {
          toast.success(`Quest completed! Received ${quest.reward.amount} ${quest.reward.currency}`,
            { duration: 5000, icon: '🎮' });
        } else if (won === false) {
          toast.error('Better luck next time! Try again to earn rewards.',
            { duration: 5000, icon: '🎮' });
        }

              // Remove the query params to prevent multiple notifications
              navigate(`/quests/${id}`, { replace: true });
              return updatedUser;
            } catch (error) {
              console.error(`Error refreshing wallet balance (attempt ${attempts + 1}):`, error);
              attempts++;
              if (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
              }
            }
          }

          if (!success) {
            toast.error('Failed to refresh wallet balance after multiple attempts');
            // Still navigate to remove query params
            navigate(`/quests/${id}`, { replace: true });
          }

          return null;
        };

        refreshAttempts();
      }
    }
  }, [quest, id, refreshWalletBalance, navigate]);

  // Check if current user has already played this quest
  const hasPlayedQuest = () => {
    if (!quest || !user) return false;

    return quest.participants.some(
      p => p.user && p.user._id && user.id && p.user._id.toString() === user.id.toString()
    );
  };

  const handlePlayQuest = async () => {
    try {
      if (!user?.isConnected) {
        toast.error('Please connect your wallet to play quests');
        return;
      }

      // Check if user has already played this quest
      if (hasPlayedQuest()) {
        toast.error('You have already played this quest');
        return;
      }

      // First join the quest to update participant counter
      console.log(`Joining quest ${id} as user ${user.address}`);
      const joinEndpoint = `/api/quests/${id}/join`;
      console.log('Join endpoint:', joinEndpoint);
      const joinResponse = await api.post(joinEndpoint, {
        address: user.address
      });

      console.log('Join quest response:', joinResponse);

      if (joinResponse) {
        // Refresh quest data to show updated participants count
        const updatedQuest = await api.get(API_ENDPOINTS.quests.details(id));
        setQuest(updatedQuest);
        console.log('Quest data refreshed:', updatedQuest);

        // Start quest attempt
        const startEndpoint = `/api/quests/${id}/start`;
        console.log('Start quest endpoint:', startEndpoint);
        await api.post(startEndpoint, {
          address: user.address
        });

        // Show success toast
        toast.success('Quest joined! Opening game...');

        // Open pool game in new window with quest and wallet info
        const gameUrl = new URL('/bitpool/index.html', window.location.origin);
        gameUrl.searchParams.set('questId', id);
        gameUrl.searchParams.set('address', user.address);
        window.open(gameUrl.toString(), '_blank');
      }
    } catch (error) {
      console.error('Error playing quest:', error);
      toast.error('Failed to join quest');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="relative">
          <div className="h-24 w-24 rounded-full border-t-4 border-l-4 border-purple-600 animate-spin"></div>
          <div className="h-24 w-24 rounded-full border-r-4 border-b-4 border-violet-500 animate-spin absolute top-0 animate-[spin_1.5s_linear_infinite]"></div>
          <div className="absolute inset-0 flex items-center justify-center text-purple-500 font-bold">
            <FaRobot className="text-2xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!id || !quest) {
    return (
      <div className="min-h-screen bg-dark py-12 relative">
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute -top-[30%] -left-[10%] w-[50%] h-[50%] bg-purple-900/10 rounded-full filter blur-[100px] animate-pulse-slow"></div>
          <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-violet-800/10 rounded-full filter blur-[100px] animate-pulse-slow"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto px-4 py-8 relative z-10"
        >
          <div className="bg-dark-300/70 backdrop-filter backdrop-blur-lg border border-violet-500/20 rounded-xl shadow-lg text-center p-12">
            <FaRobot className="text-5xl mx-auto mb-4 text-purple-400 opacity-50" />
            <h2 className="text-2xl font-bold neon-text mb-4">
              {!id ? 'Invalid Quest ID' : 'Quest Not Found'}
            </h2>
            <p className="text-gray-300 mb-6">We couldn't find the quest you're looking for.</p>
            <button
              onClick={() => navigate('/quests')}
              className="glow-button flex items-center justify-center mx-auto"
            >
              <FaChevronLeft className="mr-2" />
              Back to Quests
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const formatAddress = (address) => {
    if (!address) return 'Anonymous';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const diffStyle = difficultyConfig[quest.difficulty] || difficultyConfig.medium;

  return (
    <div className="min-h-screen bg-dark py-12 relative overflow-hidden">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-[30%] -left-[10%] w-[50%] h-[50%] bg-purple-900/10 rounded-full filter blur-[100px] animate-pulse-slow"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-violet-800/10 rounded-full filter blur-[100px] animate-pulse-slow"></div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/quests')}
            className="flex items-center text-violet-400 hover:text-violet-300 transition-colors mb-4"
          >
            <FaChevronLeft className="mr-2" />
            Back to Quests
          </button>
        </motion.div>

        <motion.div
          className="fancy-card p-8 mb-8 overflow-visible"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="relative z-10">
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${diffStyle.gradient} ${diffStyle.glow} text-white mb-4`}>
              <div className="flex items-center">
                {diffStyle.icon}
                {quest.difficulty?.charAt(0).toUpperCase() + quest.difficulty?.slice(1) || 'Medium'}
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="w-full md:w-1/3 lg:w-1/4">
                <div className="relative rounded-xl overflow-hidden aspect-square shadow-neon-violet">
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-purple-600/20"></div>
                  <img
                    src={quest.image || '/logo.png'}
                    alt={quest.name}
                    className="w-full h-full object-cover z-10 relative"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold neon-text mb-4">{quest.name}</h1>
                <p className="text-gray-300 mb-6 text-lg leading-relaxed">{quest.description}</p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-dark-300/70 backdrop-filter backdrop-blur-lg border border-violet-500/20 rounded-xl shadow-lg p-4 flex items-center">
                    <FaTrophy className="text-2xl mr-4 text-purple-400" />
                    <div>
                      <div className="text-sm text-gray-400">Reward</div>
                      <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-500">
                        {quest.reward?.amount} {quest.reward?.currency}
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-300/70 backdrop-filter backdrop-blur-lg border border-violet-500/20 rounded-xl shadow-lg p-4 flex items-center">
                    <FaUsers className="text-2xl mr-4 text-purple-400" />
                    <div>
                      <div className="text-sm text-gray-400">Played</div>
                      <div className="text-xl font-bold text-white">
                        {quest.totalPlaysCount || 0}
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-300/70 backdrop-filter backdrop-blur-lg border border-violet-500/20 rounded-xl shadow-lg p-4 flex items-center">
                    <FaUser className="text-2xl mr-4 text-purple-400" />
                    <div>
                      <div className="text-sm text-gray-400">Created by</div>
                      <div className="text-white font-medium">
                        {formatAddress(quest.createdBy?.address)}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handlePlayQuest}
                  className="glow-button flex items-center justify-center"
                >
                  <FaGamepad className="mr-2" />
                  Play Quest
                  <FaArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {leaderboard.length > 0 && (
          <motion.div
            className="fancy-card p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <FaTrophy className="text-2xl mr-3 text-violet-400" />
              <h2 className="text-2xl font-bold neon-text">
                {quest?.isAIQuest ? 'Top 20 Leaderboard' : 'Leaderboard'}
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-violet-500/20">
                    <th className="px-4 py-3 text-gray-300 font-semibold">Rank</th>
                    <th className="px-4 py-3 text-gray-300 font-semibold">Player</th>
                    <th className="px-4 py-3 text-gray-300 font-semibold text-right">Games Played</th>
                    <th className="px-4 py-3 text-gray-300 font-semibold text-right">High Score</th>
                    <th className="px-4 py-3 text-gray-300 font-semibold text-right">Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                {leaderboard.map((player, index) => (
                  <tr
                    key={player.address}
                    className={`border-b border-violet-500/10
                      ${index === 0 ? 'bg-gradient-to-r from-violet-500/5 to-purple-500/5' : ''}
                      hover:bg-gradient-to-r hover:from-violet-500/5 hover:to-purple-500/5 transition-colors`}
                  >
                    <td className="px-4 py-3 text-white">
                      {index === 0 && <FaTrophy className="inline text-yellow-500 mr-2" />}
                      {index === 1 && <FaTrophy className="inline text-gray-400 mr-2" />}
                      {index === 2 && <FaTrophy className="inline text-amber-700 mr-2" />}
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {index < 3 ? (
                        <span className={`text-transparent bg-clip-text bg-gradient-to-r ${
                          index === 0 ? 'from-yellow-400 to-yellow-600' :
                          index === 1 ? 'from-gray-300 to-gray-500' :
                          'from-amber-700 to-amber-900'
                        }`}>{formatAddress(player.address)}</span>
                      ) : (
                        <span className="text-white">{formatAddress(player.address)}</span>
                      )}
                      {player.address === user?.address && <span className="text-violet-400 ml-2">(You)</span>}
                    </td>
                    <td className="px-4 py-3 text-white text-right">{player.gamesPlayed}</td>
                    <td className="px-4 py-3 font-bold text-right">
                      {index < 3 ? (
                        <span className={`text-transparent bg-clip-text bg-gradient-to-r ${
                          index === 0 ? 'from-yellow-400 to-yellow-600' :
                          index === 1 ? 'from-gray-300 to-gray-500' :
                          'from-amber-700 to-amber-900'
                        }`}>{player.score}</span>
                      ) : (
                        <span className="text-white">{player.score}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white text-right">
                      {player.winRate}%
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default QuestDetails;
